import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { flashcardAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Skeleton } from '@mui/material';

const ViewFlashcards = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [flashcards, setFlashcards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [tags, setTags] = useState([]);
  const [categories, setCategories] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalFlashcards: 0,
    limit: 9
  });

  useEffect(() => {
    if (user) {
      fetchTags();
      fetchCategories();
      fetchFlashcards();
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchFlashcards();
    }
  }, [searchTerm, selectedTag, selectedCategory, pagination.currentPage]);


  const fetchTags = async () => {
    try {
      const response = await flashcardAPI.getMyTags();
      setTags(response.data || []);
    } catch (err) {
      console.error('Failed to fetch tags:', err);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await flashcardAPI.getMyCategories();
      setCategories(response.data || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const fetchFlashcards = async () => {
    try {
      setLoading(true);
      const response = await flashcardAPI.getMyFlashcards({
        search: searchTerm,
        tag: selectedTag,
        category: selectedCategory,
        page: pagination.currentPage,
        limit: pagination.limit
      });
      setFlashcards(response.data.flashcards || []);
      setPagination(response.data.pagination || pagination);
      setError('');
    } catch (err) {
      setError('Failed to fetch flashcards');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  if (loading) {
    return (
      <div className="container">
        <div className="card" style={{ backgroundColor: 'white' }}>
          {/* Header skeleton */}
          <div style={{ marginBottom: '24px' }}>
            <Skeleton variant="text" width="40%" height={32} sx={{ marginBottom: '8px' }} />
            <Skeleton variant="text" width="30%" height={20} />
          </div>

          {/* Search and Filter skeleton */}
          <div style={{ display: 'grid', gap: '12px', marginBottom: '20px', gridTemplateColumns: '2fr 1fr 1fr' }}>
            <Skeleton variant="rectangular" height={48} sx={{ borderRadius: '8px' }} />
            <Skeleton variant="rectangular" height={48} sx={{ borderRadius: '8px' }} />
            <Skeleton variant="rectangular" height={48} sx={{ borderRadius: '8px' }} />
          </div>

          {/* Flashcard grid skeleton */}
          <div style={{ 
            display: 'grid', 
            gap: '16px',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            marginBottom: '24px'
          }}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '10px',
                  padding: '20px',
                  border: '1px solid #e2e8f0'
                }}
              >
                {/* Title */}
                <Skeleton variant="text" width="80%" height={28} sx={{ marginBottom: '8px' }} />
                
                {/* Description lines */}
                <Skeleton variant="text" width="100%" height={18} sx={{ marginBottom: '4px' }} />
                <Skeleton variant="text" width="90%" height={18} sx={{ marginBottom: '4px' }} />
                <Skeleton variant="text" width="70%" height={18} sx={{ marginBottom: '12px' }} />
                
                {/* Tags */}
                <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
                  <Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: '12px' }} />
                  <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: '12px' }} />
                </div>
                
                {/* Category badge */}
                <Skeleton variant="rectangular" width={120} height={26} sx={{ borderRadius: '6px', marginBottom: '12px' }} />
                
                {/* Stats grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <Skeleton variant="text" width="60%" height={24} sx={{ margin: '0 auto', marginBottom: '4px' }} />
                    <Skeleton variant="text" width="80%" height={16} sx={{ margin: '0 auto' }} />
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <Skeleton variant="text" width="60%" height={24} sx={{ margin: '0 auto', marginBottom: '4px' }} />
                    <Skeleton variant="text" width="80%" height={16} sx={{ margin: '0 auto' }} />
                  </div>
                </div>
                
                {/* Action buttons */}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Skeleton variant="rectangular" height={36} sx={{ flex: 1, borderRadius: '6px' }} />
                  <Skeleton variant="rectangular" height={36} sx={{ flex: 1, borderRadius: '6px' }} />
                </div>
              </div>
            ))}
          </div>

          {/* Pagination skeleton */}
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
            <Skeleton variant="rectangular" width={80} height={36} sx={{ borderRadius: '6px' }} />
            <Skeleton variant="text" width={100} height={20} />
            <Skeleton variant="rectangular" width={80} height={36} sx={{ borderRadius: '6px' }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card" style={{ backgroundColor: 'white' }}>
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ color: '#667eea', fontSize: '22px', marginBottom: '4px' }}>🎴 My Flashcards</h2>
          <p style={{ color: '#718096', fontSize: '13px', margin: 0 }}>
            {pagination.totalFlashcards} flashcard{pagination.totalFlashcards !== 1 ? 's' : ''} available
          </p>
        </div>

        {/* Search and Filter */}
        <div style={{ display: 'grid', gap: '12px', marginBottom: '20px', gridTemplateColumns: '2fr 1fr 1fr', backgroundColor: 'white' }}>
          <input
            type="text"
            placeholder="🔍 Search flashcards..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPagination(prev => ({ ...prev, currentPage: 1 }));
            }}
            style={{
              padding: '12px 16px',
              border: '1.5px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s'
            }}
            onFocus={(e) => e.target.style.borderColor = '#667eea'}
            onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
          />
          <select
            value={selectedCategory}
            onChange={(e) => {
              setSelectedCategory(e.target.value);
              setPagination(prev => ({ ...prev, currentPage: 1 }));
            }}
            style={{
              padding: '12px 16px',
              border: '1.5px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              cursor: 'pointer',
              backgroundColor: 'white'
            }}
          >
            <option value="all">All Categories</option>
            {categories.map(category => (
              <option key={category._id} value={category._id}>{category.name}</option>
            ))}
          </select>
          <select
            value={selectedTag}
            onChange={(e) => {
              setSelectedTag(e.target.value);
              setPagination(prev => ({ ...prev, currentPage: 1 }));
            }}
            style={{
              padding: '12px 16px',
              border: '1.5px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              cursor: 'pointer',
              backgroundColor: 'white'
            }}
          >
            <option value="all">All Tags</option>
            {tags.map(tag => (
              <option key={tag._id} value={tag._id}>{tag.name}</option>
            ))}
          </select>
        </div>

        {error && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#fed7d7',
            color: '#c53030',
            borderRadius: '6px',
            marginBottom: '16px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {flashcards.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '48px 24px',
            color: '#718096',
            fontSize: '14px',
            backgroundColor: '#f7fafc',
            borderRadius: '8px',
            border: '1px solid #e2e8f0'
          }}>
            <p style={{ marginBottom: '8px', fontSize: '15px' }}>
              {searchTerm || selectedTag !== 'all' || selectedCategory !== 'all' ? 'No flashcards found matching your filters' : 'No flashcards available yet.'}
            </p>
          </div>
        ) : (
          <>
            <div style={{ 
              display: 'grid', 
              gap: '16px',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              marginBottom: '24px'
            }}>
              {flashcards.map(flashcard => (
                <div
                  key={flashcard._id}
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '10px',
                    padding: '20px',
                    border: '1px solid #e2e8f0',
                    transition: 'all 0.2s',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(102, 126, 234, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e2e8f0';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <h3 style={{
                    color: '#2d3748',
                    fontSize: '18px',
                    marginBottom: '8px',
                    fontWeight: '600'
                  }}>
                    {flashcard.title}
                  </h3>
                  <p style={{
                    color: '#718096',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    marginBottom: '12px',
                    flex: 1
                  }}>
                    {flashcard.description}
                  </p>
                  {flashcard.tags && flashcard.tags.length > 0 && (
                    <div style={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: '6px',
                      marginBottom: '12px'
                    }}>
                      {flashcard.tags.map(tag => (
                        <span
                          key={tag._id}
                          style={{
                            padding: '4px 10px',
                            backgroundColor: '#e6f2ff',
                            color: '#667eea',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}
                        >
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  )}
                  <div style={{ 
                    display: 'flex', 
                    gap: '8px',
                    marginTop: 'auto'
                  }}>
                    <button
                      onClick={() => navigate(`/flashcards/${flashcard._id}/study`)}
                      style={{
                        flex: 1,
                        padding: '10px 16px',
                        backgroundColor: '#667eea',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '600',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#5568d3'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#667eea'}
                    >
                      📚 Study
                    </button>
                    <button
                      onClick={() => navigate(`/flashcards/${flashcard._id}/match`)}
                      style={{
                        flex: 1,
                        padding: '10px 16px',
                        backgroundColor: '#38b2ac',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '600',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#319795'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#38b2ac'}
                    >
                      🎮 Match
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '8px',
                marginTop: '24px'
              }}>
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: pagination.currentPage === 1 ? '#e2e8f0' : '#667eea',
                    color: pagination.currentPage === 1 ? '#a0aec0' : 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: pagination.currentPage === 1 ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  ← Previous
                </button>
                
                <span style={{
                  padding: '8px 16px',
                  color: '#4a5568',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Page {pagination.currentPage} of {pagination.totalPages}
                </span>

                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.totalPages}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: pagination.currentPage === pagination.totalPages ? '#e2e8f0' : '#667eea',
                    color: pagination.currentPage === pagination.totalPages ? '#a0aec0' : 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: pagination.currentPage === pagination.totalPages ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ViewFlashcards;
